import { createContext, useEffect, useRef, useState } from 'react'
import { withAuthStore } from 'context/AuthStore'
import { FILTER_INIT } from 'constants/initialStates'
import { useResErrors, useStateCallback } from 'hooks'
import { deepCopyObject } from 'lib/utils'
import { AuthService } from 'lib/api'
import { useCallback } from 'react'

export const HomeStoreContext = createContext({
  homeStore: {},
  homeStoreFunc: {}
})

const HomeStoreProvider = props => {
  const _isMounted = useRef(true)
  const { standardCheck } = useResErrors()
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useStateCallback(FILTER_INIT)
  const [subjects, setSubjects] = useStateCallback([])
  const [pagination, setPagination] = useStateCallback({
    pageCount: 0,
    page: 0,
    rowsPerPage: 8
  })
  const [dictionary, setDictionary] = useStateCallback({
    branches: [],
    projects: [],
    subdivisions: [],
    roles: []
  })
  const [users, setUsers] = useStateCallback([])

  useEffect(() => {
    getInitialData()
    return () => {
      _isMounted.current = false
    }
  }, [])

  const onFilterChange = (key, value) =>
    setFilter(
      prevState => ({
        ...prevState,
        [key]: value,
        ...(key === 'projectCode' ? { roleCode: null } : {})
      }),
      updated => {
        key === 'projectCode' && onGetRoles(updated.projectCode?.code)
      }
    )

  const onFilterReset = () =>
    setFilter(deepCopyObject(FILTER_INIT), updated => {
      setPagination(prevState => ({ ...prevState, page: 0 }))
      onSearch(updated, { page: 0, rowsPerPage: pagination.rowsPerPage })()
    })

  const onPaginationChange = page =>
    setPagination(
      prevState => ({ ...prevState, page }),
      updated => {
        onSearch(filter, updated)()
      }
  )

  const changeRowsPerPageByView = (isMob = false) =>
    setPagination(
      prevState => ({
        ...prevState,
        rowsPerPage: isMob ? 3 : 8
      }),
      async updated => {
        await onSearch(filter, updated)()
      }
  )

  const onSearch =
    (data = filter, pages = pagination) =>
    async () => {
      const {
        username,
        userFullname,
        roleCode,
        subdivision,
        projectCode,
        branchCode,
        isActive,
        isSystemUser
      } = data
      const { page, rowsPerPage } = pages
      setLoading(true)

      const filter = {
        username: username || undefined,
        userFullname: userFullname || undefined,
        roleCode: roleCode?.code || undefined,
        subdivision: subdivision?.name || undefined,
        projectCode: projectCode?.code || undefined,
        branchCode: branchCode?.code || undefined,
        isActive: isActive !== 'all' ? isActive : undefined,
        isSystemUser: !isSystemUser ? undefined : isSystemUser,
        offset: page * rowsPerPage,
        limit: rowsPerPage
      }

      await AuthService.getUsers(filter).then(res =>
        standardCheck(
          res,
          () => {
            setLoading(false)
            setUsers(res.data.userList || [])
            setPagination(prevState => ({
              ...prevState,
              pageCount: res.data.countFiltered || 0
            }))
          },
          {
            onFail: () => {
              setLoading(false)
            }
          }
        )
      )
    }

  const onGetProjects = async () => {
    await AuthService.getActiveProjects().then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({
          ...prevState,
          projects: res.data || []
        }))
      })
    )
  }

  const onGetBranches = async () => {
    await AuthService.getBranches().then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({
          ...prevState,
          branches: res.data || []
        }))
      })
    )
  }

  const onGetSubdivisions = async () => {
    await AuthService.getSubdivisions().then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({
          ...prevState,
          subdivisions: res.data || []
        }))
      })
    )
  }

  const onGetRoles = async project => {
    setLoading(true)
    await AuthService.getRolesProject(project).then(res =>
      standardCheck(
        res,
        () => {
          setLoading(false)
          setDictionary(prevState => ({
            ...prevState,
            roles: res.data || []
          }))
        },
        {
          onFail: () => {
            setLoading(false)
          }
        }
      )
    )
  }

  const onChangeSubjects = useCallback((index, value) => {
    setSubjects(prevState => {
      let newArr = [...prevState]
      let newValue = value
      newArr.push({
        ...newArr[index],
        ...newValue
      })
      return newArr
    })
  })

  const onAddSubjects = useCallback(() => {
    setSubjects(prevState => {
      let newArr = [...prevState]
      newArr.push({
        "marka": null,
        "models": null,
        "yearFrom": null,
        "yearTo": null,
      })
      return newArr
    })
  })

  const onDeleteSubjects = useCallback((index) => {
    setSubjects(prevState => {
      const newArr = [...prevState]
      newArr.splice(index, 1)
      if(newArr.length === 0) {
          newArr[0] = {
              "marka": null,
              "models": null,
              "yearFrom": null,
              "yearTo": null,
          }
      }
      return newArr
    })
  })

  //find user by id and update field isActive
  const onLockUnlockUser = (id, isActive) => {
    const user = users.find(user => user.id === id)
    user.isActive = isActive
    setUsers([...users])
  }

  const getInitialData = async () => {
    setLoading(true)
    await onGetProjects()
    await onGetBranches()
    await onGetSubdivisions()
    await onSearch()
    setLoading(false)
  }

  const homeStore = {
    loading,
    filter,
    pagination,
    dictionary,
    subjects,
  }

  const homeStoreFunc = {
    onFilterChange,
    onFilterReset,
    changeRowsPerPageByView,
    onSearch: onSearch(filter),
    onPaginationChange,
    onLockUnlockUser,
    onDeleteSubjects,
    onAddSubjects,
    onChangeSubjects,
  }

  return (
    <HomeStoreContext.Provider value={{ homeStore, homeStoreFunc }}>
      {props.children}
    </HomeStoreContext.Provider>
  )
}
export default withAuthStore(HomeStoreProvider)
